<template>
  <div class="document-card" :class="documentClasses">
    <div class="document-card__topic">
      <template v-if="localAccepted || (localFilled && !localAccepted) || localNotRequired">
        <h3 class="document-card__title">
          <span v-if='alreadyAddedDocuments && alreadyAddedDocuments.length' class="mr-1">
            <theme-checkboxes name="check_all" @changed='toggleAllFiles'></theme-checkboxes>
          </span>
          {{ title }}
          <i v-if="localAccepted" class="icon-check ml-auto text-[#77b866] text-[15px]"></i>
          <span v-if="localAccepted" class='text-[#77b866] text-[14px] font-bold hidden md:inline'>{{ locales && locales.common && locales.common.approved }}</span>
        </h3>
      </template>
      <template v-if="!localAccepted && !localFilled && !localNotRequired">
        <h3 class="document-card__title">
          {{ title }}
          <i v-if="showReason" class="icon-warning ml-auto text-[#FF695B] text-[20px]"></i>
          <span v-if="showReason" class='text-[#FF695B] text-[14px] font-bold hidden md:inline'>{{ locales && locales.common && locales.common.issue_with_document }}</span>
        </h3>
      </template>
    </div>
    <div class="document-card__inner">
      <strong v-if="localAccepted" class="document-card__subheading">{{subtitle}}</strong>
      <span v-if="!localAccepted && !localFilled" class="document-card__subheading">
        {{ notificationPrefix }}
        <strong>
          <span v-if="localTypeMonths">{{ numberOfDocuments }}</span>
          {{ notification }}
        </strong>
      </span>
      <strong v-if="localFilled && !localAccepted" class="document-card__subheading">{{ cardFilledNotification }}</strong>

      <!-- default card template -->
      <div class="document-card__block" v-if="localTypeDefault">
        <!-- List of images array -->
        <div v-if='alreadyAddedDocuments && alreadyAddedDocuments.length' class="document-card__row">
          <ol class="document-card__image-list">
            <li v-for="(documentFile, index) in alreadyAddedDocuments">
              <theme-checkboxes
                :setForm="true"
                form="removeForm"
                name="remove[]"
                :options="{ [documentFile.id]: documentFile.imageName }"
                @changed='emitChange'
              ></theme-checkboxes>
              <span
                class="document-card__delete-document"
                @click="deleteAddedDocument(documentFile.id, index)"
                v-if="!documentFile.disable_remove && !disableRemove"
              >
                <i class='icon-delete'/>
                {{ locales && locales.documents && locales.documents.delete_document}}
              </span>
            </li>
          </ol>
        </div>

        <div class="document-card__row">
          <ul v-show="!checkboxState" class="document-card__image-list js-inputs-parent">
            <li v-for="(input, index) in inputsTypeFile">
              <theme-file
                inputClass="document-card__input-file"
                accept="application/pdf, image/*"
                :name="'file[' + tenantId + '][' + id + '][]'"
                :id="inputFileId + '-' + index"
                @change="fileInputChanged($event, index)"
              />
            </li>
          </ul>
          <div class="document-card__image-list-bottom">
            <button
              v-show="!checkboxState"
              class="theme-btn theme-btn--small theme-btn--tertiary"
              type="button"
              @click="addInputTypeFile"
            >
              <i class='icon-plus'/>
              {{ locales.common && locales.common.new_document }}
            </button>
            <!-- do not have document string -->
            <div v-if="showReason" class="document-card__flag-checkbox-holder">
              <input
                type="hidden"
                value=""
                :name="'reason[' + tenantId + '][' + id + ']'"
              />
              <label
                :for="inputCheckboxId"
                class="document-card__flag-checkbox-label"
                v-show="!isDocumentsAdded"
              >
                <input
                  type="checkbox"
                  class="document-card__flag-checkbox"
                  :name="inputCheckboxId"
                  :id="inputCheckboxId"
                  v-model="checkboxState"
                  @change="cardChanged"
                  :disabled="isDocumentsAdded"
                />
                <i class='document-card__flag-checkbox-icon'/>
                <span>{{ checkboxText }}</span>
              </label>
            </div>
          </div>
        </div>
        <!-- hidden textarea -->
        <div class="document-card__row" v-if="checkboxState">
          <textarea
            class="document-card__textarea"
            :id="textareaId"
            :name="'reason[' + tenantId + '][' + id + ']'"
            :placeholder="textareaPlaceholder"
            v-model="localTextareaData"
            @keyup="cardChanged"
          ></textarea>
        </div>
        <div class="document-card__buttons-group">
          <input
            type="submit"
            name="submit"
            :value="buttonValue"
            :disabled="!localFilled"
            :class="['theme-btn theme-btn--small theme-btn--primary', !localFilled && 'theme-btn--primary-disabled', loading && 'theme-btn--primary-disabled']"
            @click='loading = true'
          />
          <button
            type="submit"
            @click.prevent="requestEdrLink"
            class="theme-btn theme-btn--small theme-btn--theme"
            :formaction="edrAction"
            form="edr"
            v-if="showEdrButton"
          >
            {{ locales.users && locales.users.request_edr_link }}
          </button>
          <button
            type="submit"
            @click.prevent="requestGuarantee"
            class="theme-btn theme-btn--small theme-btn--theme"
            :formaction="guaranteeAction"
            form="guarantee"
            v-if="showGuaranteeButton"
          >
            {{ locales.documents && locales.documents.request_guarantee }}
          </button>
        </div>
      </div>

      <!-- 3 month card template -->
      <div class="document-card__block" v-if="localTypeMonths">
        <div
          class="document-card__row document-card__row--border"
          v-for="(month, parentIndex) in localMonths"
        >
          <div class="document-card__row-title">{{ month.date }}</div>
          <!-- List of images array -->
          <div v-if='month.documents && month.documents.length' class="document-card__row">
            <ol class="document-card__image-list">
              <li v-for="(documentFile, index) in month.documents">
                <span class="document-card__image-name">
                  {{ documentFile.imageName }}
                </span>
                <span
                  class="document-card__delete-document"
                  @click="deleteAddedDocument(documentFile.id, parentIndex, index)"
                  v-if="!documentFile.disable_remove && !disableRemove"
                >
                  <i class='icon-delete'/>
                  {{ locales && locales.documents && locales.documents.delete_document}}
                </span>
              </li>
            </ol>
          </div>
          <!-- choose input -->
          <div class="document-card__row">
            <ul v-show="!month.checkboxValue" class="document-card__image-list js-inputs-parent">
              <li v-for="(input, index) in monthsInputsTypeFile[parentIndex]">
                <theme-file
                  inputClass="document-card__input-file"
                  accept="application/pdf, image/*"
                  :name="'month_file[' + tenantId + '][' + id + '][' + month.index + '][]'"
                  :id="inputFileId + '-' + month.id + '-' + index"
                  @change="fileInputChanged($event, parentIndex)"
                />
              </li>
            </ul>
            <div class="document-card__image-list-bottom">
              <button
                v-show="!month.checkboxValue"
                class="theme-btn theme-btn--small theme-btn--tertiary"
                type="button"
                @click="addInputTypeFile(parentIndex)"
              >
                <i class='icon-plus'/>
                {{ locales.common && locales.common.new_document }}
              </button>
              <div v-if="showReason" class="document-card__flag-checkbox-holder">
                <input
                  type="hidden"
                  value=""
                  :name="'month_reason[' + tenantId + '][' + id + '][' + month.index + ']'"
                />
                <label
                  :for="inputCheckboxId + '-' + parentIndex"
                  class="document-card__flag-checkbox-label"
                  v-show="!month.documents.length"
                >
                  <input
                    type="checkbox"
                    class="document-card__flag-checkbox"
                    :name="inputCheckboxId + '-' + parentIndex"
                    :id="inputCheckboxId + '-' + parentIndex"
                    v-model="month.checkboxValue"
                    @change="cardChanged"
                    :disabled="!!month.documents.length"
                  />
                  <i class='document-card__flag-checkbox-icon'/>
                  <span>{{ checkboxText }}</span>
                </label>
              </div>
            </div>
          </div>
          <!-- hidden textarea -->
          <div class="document-card__row" v-if="month.checkboxValue">
            <textarea
              class="document-card__textarea"
              :id="textareaId + '-' + parentIndex"
              :name="'month_reason[' + tenantId + '][' + id + '][' + month.index + ']'"
              :placeholder="textareaPlaceholder"
              v-model="month.textareaData"
              @keyup="cardChanged"
            ></textarea>
          </div>
        </div>
        <input
          v-if="showReason"
          type="submit"
          name="submit"
          :value="buttonValue"
          :disabled="!localFilled"
          :class="['theme-btn theme-btn--small theme-btn--primary', !localFilled && 'theme-btn--primary-disabled', loading && 'theme-btn--primary-disabled']"
          @click='loading = true'
        />
      </div>

      <!-- alternate card template -->
      <div class="document-card__block" v-if="localTypeAlternate">
        <!-- List of images array -->
        <div v-if='alreadyAddedDocuments && alreadyAddedDocuments.length' class="document-card__row document-card__row--border">
          <ol class="document-card__image-list">
            <li v-for="(documentFile, index) in alreadyAddedDocuments">
              <span class="document-card__image-name">
                {{ documentFile.imageName }}
              </span>
              <span
                class="document-card__delete-document"
                @click="deleteAddedDocument(documentFile.id, index)"
                v-if="!documentFile.disable_remove && !disableRemove"
                >
                <i class='icon-delete'/>
                {{ locales && locales.documents && locales.documents.delete_document}}
              </span>
            </li>
          </ol>
        </div>
        <!-- choose input -->
        <div class="document-card__row document-card__row--border">
          <ul v-show="!checkboxState" class="document-card__image-list js-inputs-parent">
            <li v-for="(input, index) in inputsTypeFile">
              <theme-file
                inputClass="document-card__input-file"
                accept="application/pdf, image/*"
                :name="'file[' + tenantId + '][' + id + '][]'"
                :id="inputFileId + '-' + index"
                @change="fileInputChanged"
              />
            </li>
          </ul>
          <div class="document-card__image-list-bottom">
            <button
              v-show="!checkboxState"
              class="theme-btn theme-btn--small theme-btn--tertiary"
              type="button"
              @click="addInputTypeFile"
            >
              <i class='icon-plus'/>
              {{ locales.common && locales.common.new_document }}
            </button>
            <div v-if="showReason" class="document-card__flag-checkbox-holder">
              <input
                type="hidden"
                value=""
                :name="'reason[' + tenantId + '][' + id + ']'"
              />
              <label
                :for="inputCheckboxId"
                class="document-card__flag-checkbox-label"
                v-show="!isDocumentsAdded || !isAlternateFormFilled"
                >
                <input
                  type="checkbox"
                  class="document-card__flag-checkbox"
                  :name="inputCheckboxId"
                  :id="inputCheckboxId"
                  v-model="checkboxState"
                  @change="cardChanged"
                  :disabled="isDocumentsAdded || isAlternateFormFilled"
                />
                <i class='document-card__flag-checkbox-icon'/>
                <span>{{ checkboxText }}</span>
              </label>
            </div>
          </div>
          <!-- hidden textarea -->
          <div class="document-card__row" v-if="checkboxState">
            <textarea
              class="document-card__textarea"
              :id="textareaId"
              :name="'reason[' + tenantId + '][' + id + ']'"
              :placeholder="textareaPlaceholder"
              v-model="localTextareaData"
              @keyup="cardChanged"
            ></textarea>
          </div>
        </div>
        <!-- small form -->
        <div class="document-card__row">
          <div class="document-card__form-title">{{ localSmallFormDescription }}</div>
          <div class="document-card__form">
            <div
              v-for="row in localSmallForm"
              class="document-card__form-box"
            >
              <label
                class="document-card__textfield-label"
                :for="textFieldId + '-' + row.id"
              >
                {{ row.labelText }}
              </label>
              <input
                class="document-card__textfield"
                type="text"
                :name="row.fieldName"
                :id="textFieldId + '-' + row.id"
                @keyup="cardChanged"
                v-model="row.inputData"
              />
            </div>
          </div>
        </div>
        <div class="document-card__buttons-group">
          <input
            v-if="showReason"
            type="submit"
            name="submit"
            :value="buttonValue"
            :disabled="!localFilled"
            :class="['theme-btn theme-btn--small theme-btn--primary', !localFilled && 'theme-btn--primary-disabled', loading && 'theme-btn--primary-disabled']"
            @click='loading = true'
          />
          <button
            type="submit"
            @click.prevent="sendSurvey"
            :disabled="disabled"
            :class="['theme-btn theme-btn--small theme-btn--theme', disabled && 'theme-btn--theme-disabled']"
            :formaction="surveyAction"
            form="edr"
            v-if="showSurveyButton"
          >
              {{
                locales.documents &&
                locales.documents.send_good_tenant_survey_to_landlord
              }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";
import $ from "jquery";
import sweetalert from "sweetalert";
export default {
  data() {
    return {
      alreadyAddedDocuments: this.addedDocuments,
      inputsTypeFile: [0],
      fileNames: [],
      monthsInputsTypeFile: [[0], [0], [0]],
      checkboxState: this.checkboxValue,
      localTextareaData: this.textareaData,
      localMonths: this.months,
      localSmallFormDescription: this.smallFormDescription,
      localSmallForm: this.smallForm,
      localAccepted: this.accepted,
      localNotRequired: this.notRequired,
      isDocumentAttached: false,
      disabled: this.surveyDisabled,
      loading: false,
    };
  },
  computed: {
    ...mapGetters(["metadata", "locales"]),
    numberOfDocuments: function () {
      return 3 - this.numberOfMonthsFilled;
    },
    documentClasses: function () {
      return {
        "document-card_accepted": this.localAccepted,
        "document-card_filled": this.setFilledClass,
        "document-card_declined": this.setDeclinedClass,
        "document-card_can-be-confirmed":
          this.localAccepted || this.setFilledClass || this.localNotRequired,
      };
    },
    setFilledClass: function () {
      if ((this.localFilled || this.localNotRequired) && !this.localAccepted) {
        return true;
      } else {
        return false;
      }
    },
    setDeclinedClass: function () {
      if (!this.localAccepted && !this.localFilled && !this.localNotRequired) {
        return true;
      } else {
        return false;
      }
    },
    inputFileId: function () {
      return "document-" + this.id + this.tenantId;
    },
    inputCheckboxId: function () {
      return "checkbox-" + this.id + this.tenantId;
    },
    textFieldId: function () {
      return "textfield-" + this.id + this.tenantId;
    },
    textareaId: function () {
      return "textarea-" + this.id + this.tenantId;
    },
    isDocumentsAdded: function () {
      return !!this.alreadyAddedDocuments.length;
    },
    isAlternateFormFilled: function () {
      var alternateForm = [false, false, false];

      for (var i = 0; i < 3; i++) {
        if (this.localSmallForm[i].inputData.length) {
          alternateForm[i] = true;
        }
      }

      if (
        alternateForm[0] === true &&
        alternateForm[1] === true &&
        alternateForm[2] === true
      ) {
        return true;
      } else {
        return false;
      }
    },
    numberOfMonthsFilled: function () {
      var month = 0;

      for (var i = 0; i < 3; i++) {
        if (this.localMonths[i].documents.length) {
          month++;
        } else {
          if (
            this.localMonths[i].textareaData.length &&
            this.localMonths[i].checkboxValue
          ) {
            month++;
          }
        }
      }

      return month;
    },
    isMonthsFilled: function () {
      var month = [false, false, false];

      for (var i = 0; i < 3; i++) {
        if (
          this.localMonths[i].documents.length ||
          this.localMonths[i].fileInput.length
        ) {
          month[i] = true;
        } else {
          if (
            this.localMonths[i].textareaData.length &&
            this.localMonths[i].checkboxValue
          ) {
            month[i] = true;
          } else {
            month[i] = false;
          }
        }
      }

      if (month[0] && month[1] && month[2]) {
        return true;
      } else {
        return false;
      }
    },
    localFilled: function () {
      if (this.localTypeMonths) {
        if (this.isMonthsFilled) {
          return true;
        } else {
          return false;
        }
      } else if (this.localTypeAlternate) {
        if (this.alreadyAddedDocuments.length === 0) {
          if (
            (this.localTextareaData != "" && this.checkboxState) ||
            this.isAlternateFormFilled ||
            this.isDocumentAttached
          ) {
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      } else {
        if (this.alreadyAddedDocuments.length === 0) {
          if (
            (this.localTextareaData != "" && this.checkboxState) ||
            this.isDocumentAttached
          ) {
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      }
    },
    localTypeMonths: function () {
      return this.type === "months";
    },
    localTypeDefault: function () {
      return this.type === "default";
    },
    localTypeAlternate: function () {
      return this.type === "alternate";
    },
  },
  props: {
    id: {
      default: "",
      type: String,
    },
    tenantId: {
      default: "",
      type: String,
    },
    title: {
      default: "",
      type: String,
    },
    accepted: {
      default: false,
      type: Boolean,
    },
    notRequired: {
      default: false,
      type: Boolean,
    },
    subtitle: {
      default: "",
      type: String,
    },
    notification: {
      default: "",
      type: String,
    },
    notificationPrefix: {
      default: "",
      type: String,
    },
    buttonValue: {
      default: "",
      type: String,
    },
    addedDocuments: {
      default: () => [],
      type: Array,
    },
    checkboxText: {
      default: "",
      type: String,
    },
    checkboxValue: {
      default: false,
      type: Boolean,
    },
    textareaPlaceholder: {
      default: "",
      type: String,
    },
    textareaData: {
      default: "",
      type: String,
    },
    type: {
      default: "",
      type: String,
    },
    months: {
      default: () => [],
      type: Array,
    },
    smallFormDescription: {
      default: "",
      type: String,
    },
    smallForm: {
      default: () => [],
      type: Array,
    },
    cardFilledNotification: {
      default: "",
      type: String,
    },
    showReason: {
      default: true,
      type: Boolean,
    },
    edrAction: {
      default: "",
      type: String,
    },
    showEdrButton: {
      default: false,
      type: Boolean,
    },
    guaranteeAction: {
      default: "",
      type: String,
    },
    showGuaranteeButton: {
      default: false,
      type: Boolean,
    },
    surveyAction: {
      default: "",
      type: String,
    },
    showSurveyButton: {
      default: false,
      type: Boolean,
    },
    disableRemove: {
      default: false,
      type: Boolean,
    },
    surveyDisabled: {
      default: false,
      type: Boolean,
    },
  },
  methods: {
    addInputTypeFile: function (index) {
      if (this.localTypeMonths) {
        this.monthsInputsTypeFile[index].push(1);
      } else {
        this.inputsTypeFile.push(1);
      }
    },
    deleteAddedDocument: function (id, index, index2) {
      window.app
        .sweetalert(
          this.locales &&
            this.locales.users &&
            this.locales.users.are_you_sure_you_want_to_delete_this_document,
          {
            buttons: ["Cancel", "Ok"],
          }
        )
        .then((value) => {
          if (value) {
            if (this.localTypeMonths) {
              this.localMonths[index].documents.splice(index2, 1);
              if (!this.localMonths[index].documents.length)
                this.localAccepted = false;
            } else {
              this.alreadyAddedDocuments.splice(index, 1);
              if (!this.alreadyAddedDocuments.length)
                this.localAccepted = false;
            }
            $.ajax({
              url: `/attachments/delete/` + id,
              type: "DELETE",
              success: function (response) {},
              error: function (response) {},
            });
          }
        });
    },
    cardChanged: function () {
      this.localAccepted = false;
    },
    fileInputChanged: function (event, index) {
      var parent = event.target.closest('.js-inputs-parent');
      var childList = parent.getElementsByClassName("document-card__input-file");
      if (this.localTypeMonths) {
        for (var i = 0; i < childList.length; i++) {
          if (childList[i].value != "") {
            this.localMonths[index].fileInput = "filled";
            break;
          } else {
            this.localMonths[index].fileInput = "";
          }
        }
      } else {
        for (var i = 0; i < childList.length; i++) {
          if (childList[i].value != "") {
            this.isDocumentAttached = true;
            break;
          } else {
            this.isDocumentAttached = false;
          }
        }
      }
    },
    requestEdrLink: function (e) {
      e.preventDefault();
      var action = e.target.getAttribute("formaction");
      console.log(action);
      document.querySelector(".js-loader-item").style.display = "";
      var that = this;
      var message = window.app.locales.users.edr_link_sent;
      $.ajax({
        url: action,
        method: "post",
        success: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          window.app.sweetalert(
            window.app.locales.common.success,
            message,
            "success"
          );
        },
        error: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          var message = window.app.locales.common.error;
          if (result.responseJSON.error) {
            message = result.responseJSON.error;
          }
          window.app
            .sweetalert(window.app.locales.common.error, message, "error")
            .then((value) => {
              if (result.responseJSON.redirect) {
                window.location.assign(result.responseJSON.redirect);
              }
            });
        },
      });
    },
    requestGuarantee: function (e) {
      e.preventDefault();
      var action = e.target.getAttribute("formaction");
      console.log(action);
      document.querySelector(".js-loader-item").style.display = "";
      var that = this;
      var message = window.app.locales.documents.guarantee_sent;
      $.ajax({
        url: action,
        method: "post",
        success: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          window.app.sweetalert(
            window.app.locales.common.success,
            message,
            "success"
          );
        },
        error: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          var message = window.app.locales.common.error;
          if (result.responseJSON.error) {
            message = result.responseJSON.error;
          }
          window.app
            .sweetalert(window.app.locales.common.error, message, "error")
            .then((value) => {
              if (result.responseJSON.redirect) {
                window.location.assign(result.responseJSON.redirect);
              }
            });
        },
      });
    },
    sendSurvey: function (e) {
      e.preventDefault();
      var action = e.target.getAttribute("formaction");
      document.querySelector(".js-loader-item").style.display = "";
      var that = this;
      var message = window.app.locales.documents.survey_sent;
      that.disabled=true;
      $.ajax({
        url: action,
        method: "post",
        success: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          window.app.sweetalert(
            window.app.locales.common.success,
            message,
            "success"
          ).then((value) => {
            that.disabled=false;
          });
        },
        error: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          var message = window.app.locales.common.error;
          if (result.responseJSON.error) {
            message = result.responseJSON.error;
          }
          window.app
            .sweetalert(window.app.locales.common.error, message, "error")
            .then((value) => {
              if (result.responseJSON.redirect) {
                window.location.assign(result.responseJSON.redirect);
              }
              that.disabled=false;
            });
        },
      });
    },
    emitChange: function(value) {
      this.$emit('change', value);
    },
    toggleAllFiles: function() {
      const root = this.$el;
      const initialInput = root.querySelector('input[name="check_all"][type="checkbox"]');
      const inputs = root.querySelectorAll('input[name="remove[]"][form="removeForm"][type="checkbox"]');

      inputs.forEach(input => {
        if (initialInput.checked) {
          if(!input.checked) {
            input.click();
          }
        } else {
          if(input.checked) {
            input.click();
          }
        }
      });
    }
  },
  watch: {
    documentClasses: function () {
      var event = new CustomEvent("card-changed", {
        detail: "Triggered on card change",
      });
      setTimeout(function () {
        document.dispatchEvent(event);
      }, 0);
    },
  },
};
</script>


// WEBPACK FOOTER //
// resources/js/components/VDocumentCard.vue